import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { AppLayout } from "@/components/AppLayout";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { AuthGuard, PermissionGuard } from "@/auth/ProtectedRoute";
import { useAuth } from "@/auth/AuthContext";
import { defaultLandingPath } from "@/auth/permissions";
import { ClaimsPage } from "@/pages/ClaimsPage";
import { ClientsPage } from "@/pages/ClientsPage";
import { DashboardPage } from "@/pages/DashboardPage";
import { LoginPage } from "@/pages/LoginPage";
import { MyBookPage } from "@/pages/MyBookPage";
import { MyWorkPage } from "@/pages/MyWorkPage";
import { PipelinePage } from "@/pages/PipelinePage";
import { PoliciesPage } from "@/pages/PoliciesPage";
import { RenewalsPage } from "@/pages/RenewalsPage";
import { DocumentsPage } from "@/pages/DocumentsPage";
import { SettingsPage } from "@/pages/SettingsPage";
import { QuotesPage } from "@/pages/QuotesPage";
import { RateShopPage } from "@/pages/RateShopPage";
import { SubmissionsPage } from "@/pages/SubmissionsPage";
import { CommissionsPage } from "@/pages/CommissionsPage";

const ClaimDetailPage = lazy(() => import("@/pages/ClaimDetailPage").then((m) => ({ default: m.ClaimDetailPage })));
const ClientDetailPage = lazy(() => import("@/pages/ClientDetailPage").then((m) => ({ default: m.ClientDetailPage })));
const PolicyDetailPage = lazy(() => import("@/pages/PolicyDetailPage").then((m) => ({ default: m.PolicyDetailPage })));
const ReportsPage = lazy(() => import("@/pages/ReportsPage").then((m) => ({ default: m.ReportsPage })));
const AccountingPage = lazy(() => import("@/pages/AccountingPage").then((m) => ({ default: m.AccountingPage })));
const QuickBooksPage = lazy(() => import("@/pages/QuickBooksPage").then((m) => ({ default: m.QuickBooksPage })));
const AdminPage = lazy(() => import("@/pages/AdminPage").then((m) => ({ default: m.AdminPage })));
const BillingPage = lazy(() => import("@/pages/BillingPage").then((m) => ({ default: m.BillingPage })));
const DataMigrationPage = lazy(() =>
  import("@/pages/DataMigrationPage").then((m) => ({ default: m.DataMigrationPage })),
);

function PageFallback() {
  return <div style={{ padding: "2rem", color: "var(--muted)" }}>Loading…</div>;
}

function RoleAwareHome() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  const landing = defaultLandingPath(user);
  if (landing === "/") return <DashboardPage />;
  return <Navigate to={landing} replace />;
}

export default function App() {
  return (
    <ErrorBoundary>
      <Suspense fallback={<PageFallback />}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route element={<AuthGuard />}>
            <Route element={<AppLayout />}>
              <Route index element={<RoleAwareHome />} />
              <Route element={<PermissionGuard permission="crm.view" />}>
                <Route path="clients" element={<ClientsPage />} />
                <Route path="clients/:customerId" element={<ClientDetailPage />} />
                <Route path="my-book" element={<MyBookPage />} />
                <Route path="pipeline" element={<PipelinePage />} />
                <Route path="rate-shop" element={<RateShopPage />} />
                <Route path="quotes" element={<QuotesPage />} />
                <Route path="submissions" element={<SubmissionsPage />} />
                <Route path="policies" element={<PoliciesPage />} />
                <Route path="renewals" element={<RenewalsPage />} />
                <Route path="policies/:policyId" element={<PolicyDetailPage />} />
              </Route>
              <Route element={<PermissionGuard permission="tasks.view" />}>
                <Route path="my-work" element={<MyWorkPage />} />
              </Route>
              <Route element={<PermissionGuard permission="claims.view" />}>
                <Route path="claims" element={<ClaimsPage />} />
                <Route path="claims/:claimId" element={<ClaimDetailPage />} />
              </Route>
              <Route element={<PermissionGuard permission="documents.view" />}>
                <Route path="documents" element={<DocumentsPage />} />
              </Route>
              <Route element={<PermissionGuard permission="commissions.view" />}>
                <Route path="commissions" element={<CommissionsPage />} />
              </Route>
              <Route element={<PermissionGuard permission="accounting.view" />}>
                <Route path="accounting" element={<AccountingPage />} />
              </Route>
              <Route element={<PermissionGuard permission="accounting.manage" />}>
                <Route path="settings/quickbooks" element={<QuickBooksPage />} />
              </Route>
              <Route element={<PermissionGuard permission="reports.view" />}>
                <Route path="reports" element={<ReportsPage />} />
              </Route>
              <Route element={<PermissionGuard permission="users.manage" />}>
                <Route path="admin" element={<AdminPage />} />
              </Route>
              <Route element={<PermissionGuard permission="agency.manage" />}>
                <Route path="settings/billing" element={<BillingPage />} />
                <Route path="settings/data-migration" element={<DataMigrationPage />} />
              </Route>
              <Route path="settings" element={<SettingsPage />} />
            </Route>
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}
